import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Search, Mail, Briefcase, MapPin, Clock, X, Phone, MessageSquare, MessageCircle, Calendar, Cake, UserRound, FileText, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { localTimeIn } from "@/lib/timezones";

const ROLE_COLORS = {
  admin: "bg-[#ff000022] text-[color:var(--accent)]",
  manager: "bg-[#00e5ff20] text-[#00e5ff]",
  employee: "bg-[#242c3d] text-slate-300",
};

export default function Directory() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [positions, setPositions] = useState([]);
  const [presence, setPresence] = useState({});
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [location, setLocation] = useState("all");
  const [openId, setOpenId] = useState(null);

  const loadUsers = () => api.get("/users").then(({ data }) => setUsers(data));
  const loadPositions = () => api.get("/positions").then(({ data }) => setPositions(data)).catch(() => {});

  useEffect(() => {
    loadUsers();
    loadPositions();
    const loadPresence = () => api.get("/presence").then(({ data }) => {
      setPresence(Object.fromEntries(data.map((p) => [p.user_id, p])));
    }).catch(() => {});
    loadPresence();
    const t = setInterval(loadPresence, 15000);
    return () => clearInterval(t);
  }, []);

  const positionsById = useMemo(
    () => Object.fromEntries(positions.map((p) => [p.id, p])),
    [positions]
  );

  const handleCardClick = (u) => {
    // Mobile users go straight into a DM (fastest path). Desktop keeps the rich profile modal.
    if (typeof window !== "undefined" && window.innerWidth < 1024) {
      navigate(`/messages?dm=${u.id}`);
    } else {
      setOpenId(u.id);
    }
  };

  const locations = useMemo(() => {
    const set = new Set(users.map((u) => u.location).filter(Boolean));
    return ["all", ...Array.from(set)];
  }, [users]);

  const filtered = useMemo(() => {
    return users.filter((u) => {
      const matchesQ =
        !q ||
        [u.name, u.email, u.title, u.department, u.location]
          .filter(Boolean)
          .some((x) => x.toLowerCase().includes(q.toLowerCase()));
      const matchesR = role === "all" || u.role === role;
      const matchesL = location === "all" || u.location === location;
      return matchesQ && matchesR && matchesL;
    });
  }, [users, q, role, location]);

  const openUser = users.find((u) => u.id === openId) || null;
  const managerOfOpen = openUser?.manager_id ? users.find((u) => u.id === openUser.manager_id) : null;

  return (
    <div className="space-y-6" data-testid="directory-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-[color:var(--accent)]">People</div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Employee Directory</h1>
      </div>

      <div className="card-surface p-4 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="directory-search"
            placeholder="Search by name, email, title, department, or location…"
            className="w-full pl-10 pr-4 py-2.5 bg-[#0b0d12] border border-[#2a3447] rounded-lg outline-none focus:border-[color:var(--accent)]"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {["all", "admin", "manager", "employee"].map((r) => (
            <button
              key={r}
              onClick={() => setRole(r)}
              data-testid={`filter-${r}`}
              className={`px-4 py-2 text-xs uppercase tracking-widest rounded-lg border transition-colors ${
                role === r ? "border-[color:var(--accent)] text-[color:var(--accent)] bg-[#ff000012]" : "border-[#2a3447] text-slate-400 hover:text-slate-200"
              }`}
            >
              {r}
            </button>
          ))}
        </div>
        {locations.length > 1 && (
          <select
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            data-testid="filter-location"
            className="bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-[color:var(--accent)]"
          >
            {locations.map((l) => (
              <option key={l} value={l}>
                {l === "all" ? "All locations" : l}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="directory-list">
        {filtered.map((u) => {
          const initials = (u.name || u.email).split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
          const online = presence[u.id]?.online;
          return (
            <div
              key={u.id}
              role="button"
              tabIndex={0}
              onClick={() => handleCardClick(u)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleCardClick(u); } }}
              className="card-surface overflow-hidden hover:border-[#ff000044] transition-colors text-left relative group cursor-pointer focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]/60"
              data-testid={`user-card-${u.id}`}
            >
              {u.cover_url ? (
                <img src={u.cover_url} alt="" className="w-full h-20 object-cover" data-testid={`user-cover-${u.id}`} />
              ) : (
                <div className="cover-fallback w-full h-20" data-testid={`user-cover-fallback-${u.id}`} />
              )}
              <div className="px-5 pt-3 pb-5">
              <div className="flex items-end gap-4 -mt-11">
                <div className="relative shrink-0" data-testid={`presence-dot-${u.id}`}>
                  {u.avatar_url ? (
                    <img src={u.avatar_url} alt={u.name} className="w-14 h-14 rounded-full object-cover border-4 border-[#121620]" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-[#242c3d] flex items-center justify-center font-bold text-[color:var(--accent)] text-lg border-4 border-[#121620]">
                      {initials}
                    </div>
                  )}
                  <span
                    data-testid={`presence-${u.id}`}
                    title={online ? "Online" : "Offline"}
                    className={`absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-[#0b0d12] ${online ? "bg-[color:var(--accent)]" : "bg-slate-600"}`}
                  />
                </div>
                <div className="min-w-0 flex-1 pb-1">
                  <div className="flex items-center gap-2">
                    <div className="font-bold truncate">{u.name}</div>
                    <span className={`pill ${ROLE_COLORS[u.role] || ROLE_COLORS.employee}`}>{u.role}</span>
                  </div>
                  {u.position_id && positionsById[u.position_id] && (
                    <div className="text-[11px] uppercase tracking-widest text-[color:var(--accent)] font-semibold mt-1 inline-flex items-center gap-1" data-testid={`user-position-${u.id}`}>
                      <Briefcase size={11} /> {positionsById[u.position_id].name}
                    </div>
                  )}
                  <div className="text-sm text-slate-300 mt-1 flex items-center gap-1.5">
                    <Briefcase size={12} /> {u.job || u.title || "—"}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{u.department || "No department"}</div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                    {u.location && (
                      <span className="flex items-center gap-1"><MapPin size={12} /> {u.location}</span>
                    )}
                    {u.timezone && localTimeIn(u.timezone) && (
                      <span className="flex items-center gap-1"><Clock size={12} /> {localTimeIn(u.timezone)}</span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); navigate(`/messages?dm=${u.id}`); }}
                  data-testid={`card-dm-${u.id}`}
                  title={`Message ${u.name}`}
                  className="opacity-0 group-hover:opacity-100 lg:flex hidden shrink-0 w-9 h-9 rounded-lg border border-[#2a3447] items-center justify-center text-slate-300 hover:text-black hover:bg-[color:var(--accent)] hover:border-[color:var(--accent)] transition-all mb-1"
                >
                  <MessageSquare size={14} />
                </button>
              </div>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <div className="text-slate-500 text-sm col-span-full">No employees match your filters.</div>}
      </div>

      {openUser && <ProfileModal user={openUser} manager={managerOfOpen} positions={positions} position={openUser.position_id ? positionsById[openUser.position_id] : null} currentUser={currentUser} onClose={() => setOpenId(null)} onAssigned={loadUsers} />}
    </div>
  );
}

function ProfileModal({ user, manager, positions, position, currentUser, onClose, onAssigned }) {
  const navigate = useNavigate();
  const [showAgreement, setShowAgreement] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const canAssign = currentUser?.role === "admin" || currentUser?.role === "manager";
  const initials = (user.first_name || user.name || user.email).split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const clock = localTimeIn(user.timezone);
  const displayName = user.first_name || user.name;
  const messageThem = () => {
    onClose();
    navigate(`/messages?dm=${user.id}`);
  };
  const assignPosition = async (positionId) => {
    setAssigning(true);
    try {
      await api.post(`/users/${user.id}/position`, { position_id: positionId || null });
      toast.success(positionId ? "Position assigned" : "Position cleared");
      onAssigned && onAssigned();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Assign failed");
    } finally {
      setAssigning(false);
    }
  };
  const fmtDate = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
      return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    } catch { return iso; }
  };
  const fmtBirthday = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso + "T00:00:00");
      return d.toLocaleDateString(undefined, { month: "long", day: "numeric" });
    } catch { return iso; }
  };
  const tenureYears = (iso) => {
    if (!iso) return null;
    try {
      const start = new Date(iso + "T00:00:00");
      const years = (Date.now() - start.getTime()) / (365.25 * 24 * 3600 * 1000);
      if (years < 1/12) return "just joined";
      if (years < 1) return `${Math.floor(years * 12)} mo`;
      return `${years.toFixed(1)} yrs`;
    } catch { return null; }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="card-surface w-full max-w-lg overflow-hidden relative" onClick={(e) => e.stopPropagation()} data-testid="profile-modal">
        <button onClick={onClose} data-testid="profile-modal-close" className="absolute top-3 right-3 z-10 text-white/90 hover:text-white bg-black/50 hover:bg-black/80 backdrop-blur w-8 h-8 rounded-full grid place-items-center transition-colors">
          <X size={16} />
        </button>
        {user.cover_url ? (
          <img src={user.cover_url} alt="" className="w-full h-32 object-cover" data-testid="profile-modal-cover" />
        ) : (
          <div className="cover-fallback w-full h-32" data-testid="profile-modal-cover-fallback" />
        )}
        <div className="relative px-6 pt-3 pb-4 border-b border-slate-800">
          <div className="flex items-end gap-4 -mt-14">
            {user.avatar_url ? (
              <img src={user.avatar_url} alt={displayName} className="w-20 h-20 rounded-full object-cover border-4 border-[#121620]" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-[#242c3d] grid place-items-center font-black text-2xl border-4 border-[#121620]" style={{ color: "var(--accent)" }}>
                {initials}
              </div>
            )}
            <div className="flex-1 min-w-0 pb-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-2xl font-black truncate">{displayName}</h3>
                <span className={`pill ${ROLE_COLORS[user.role] || ROLE_COLORS.employee}`}>{user.role}</span>
              </div>
              {user.discord_name && (
                <div className="text-sm mt-1 inline-flex items-center gap-1.5" style={{ color: "var(--accent)" }}>
                  <MessageCircle size={12} /> {user.discord_name}
                </div>
              )}
              {user.title && <div className="text-sm text-slate-300 mt-1">{user.title}</div>}
              {user.job && <div className="text-xs text-slate-400 mt-0.5 inline-flex items-center gap-1"><Briefcase size={11} /> {user.job}</div>}
              {user.department && <div className="text-xs text-slate-500 mt-0.5">{user.department}</div>}
            </div>
          </div>
        </div>

        <div className="p-6 space-y-3 text-sm">
          {/* POSITION */}
          {(position || canAssign) && (
            <div className="pb-3 border-b border-slate-800 space-y-2" data-testid="modal-position-block">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded bg-[color:var(--accent)]/15 border border-[color:var(--accent)]/40 grid place-items-center text-[color:var(--accent)] shrink-0 mt-0.5">
                  <Briefcase size={12} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Position</div>
                  <div className="text-slate-200 font-semibold">
                    {position ? position.name : <span className="text-slate-500 font-normal">Not assigned</span>}
                  </div>
                  {position?.description && <div className="text-xs text-slate-400 mt-0.5">{position.description}</div>}
                </div>
                {canAssign && positions?.length > 0 && (
                  <select
                    value={user.position_id || ""}
                    onChange={(e) => assignPosition(e.target.value)}
                    disabled={assigning}
                    data-testid="modal-assign-position"
                    className="bg-[#0b0d12] border border-[#2a3447] rounded px-2 py-1 text-xs shrink-0 focus:border-[color:var(--accent)] outline-none disabled:opacity-60"
                  >
                    <option value="">— None —</option>
                    {positions.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                )}
              </div>
              {position && (position.agreement_body || position.agreement_url) && (
                <div className="pl-9 flex items-center gap-3">
                  {position.agreement_body && (
                    <button
                      type="button"
                      onClick={() => setShowAgreement((s) => !s)}
                      data-testid="modal-view-agreement"
                      className="text-xs inline-flex items-center gap-1.5 text-[color:var(--accent)] hover:underline"
                    >
                      <FileText size={12} /> {showAgreement ? "Hide agreement" : "View staff agreement"}
                    </button>
                  )}
                  {position.agreement_url && (
                    <a
                      href={position.agreement_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="modal-agreement-url"
                      className="text-xs inline-flex items-center gap-1.5 text-slate-400 hover:text-[color:var(--accent)]"
                    >
                      <LinkIcon size={12} /> External copy
                    </a>
                  )}
                </div>
              )}
              {showAgreement && position?.agreement_body && (
                <div className="pl-9" data-testid="modal-agreement-body">
                  <pre className="text-[12px] text-slate-300 bg-[#0b0d12] border border-slate-800 rounded-lg p-3 whitespace-pre-wrap font-mono leading-relaxed max-h-72 overflow-y-auto">
                    {position.agreement_body}
                  </pre>
                </div>
              )}
              {user.position_assigned_at && (
                <div className="pl-9 text-[10px] uppercase tracking-widest text-slate-500">
                  Assigned {new Date(user.position_assigned_at).toLocaleDateString()}
                </div>
              )}
            </div>
          )}
          <Row icon={<Mail size={14} />} label="Login email"><a href={`mailto:${user.email}`} className="hover:text-[color:var(--accent)]">{user.email}</a></Row>
          {user.work_email && user.work_email !== user.email && (
            <Row icon={<Mail size={14} />} label="Work email"><a href={`mailto:${user.work_email}`} className="hover:text-[color:var(--accent)]">{user.work_email}</a></Row>
          )}
          {user.personal_email && (
            <Row icon={<Mail size={14} />} label="Personal email"><a href={`mailto:${user.personal_email}`} className="hover:text-[color:var(--accent)]">{user.personal_email}</a></Row>
          )}
          {user.phone && <Row icon={<Phone size={14} />} label="Phone"><a href={`tel:${user.phone}`} className="hover:text-[color:var(--accent)]">{user.phone}</a></Row>}
          {user.location && <Row icon={<MapPin size={14} />} label="Location">{user.location}</Row>}
          {user.timezone && (
            <Row icon={<Clock size={14} />} label="Timezone">
              {user.timezone} {clock && <span className="text-[color:var(--accent)]"> · now {clock}</span>}
            </Row>
          )}
          {user.start_date && (
            <Row icon={<Calendar size={14} />} label="Start date">
              {fmtDate(user.start_date)}
              {tenureYears(user.start_date) && <span className="text-slate-500"> · {tenureYears(user.start_date)}</span>}
            </Row>
          )}
          {user.birthdate && (
            <Row icon={<Cake size={14} />} label="Birthday">
              {fmtBirthday(user.birthdate)}
            </Row>
          )}
          {manager && <Row icon={<UserRound size={14} />} label="Reports to">{manager.name}</Row>}
          {user.bio && (
            <div className="pt-3 border-t border-slate-800">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">About</div>
              <p className="text-slate-300 leading-relaxed">{user.bio}</p>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-800 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200">Close</button>
          <button onClick={messageThem} data-testid="profile-modal-message" className="px-4 py-2 rounded-lg font-bold text-black text-sm flex items-center gap-2" style={{ background: "var(--accent)" }}>
            <MessageSquare size={14} /> Send message
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ icon, label, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-6 h-6 rounded bg-[#0b0d12] border border-slate-800 grid place-items-center text-slate-400 shrink-0 mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
        <div className="text-slate-200 truncate">{children}</div>
      </div>
    </div>
  );
}
